import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getIsCanadaWebsite } from "@midland-industries/Components/WebsiteSelector";
import { CustomDynamicFormState } from "@midland-industries/Store/Data/DynamicForms/DynamicFormReducer";
import setDynamicFormFieldValue from "@midland-industries/Store/Data/DynamicForms/Handlers/SetDynamicFormFieldValue";
import {
    DynamicFormButtonShapes,
    DynamicFormButtonShapesRectangle,
    DynamicFormButtonTypes,
    DynamicFormButtonTypesSolid,
    DynamicFormButtonVariants,
    DynamicFormButtonVariantsPrimary,
    DynamicFormCheckboxGroupLabelPositionsRight,
    DynamicFormCheckboxLabelPositions,
    DynamicFormCheckboxLabelPositionsNone,
    DynamicFormCheckboxLabelPositionsRight,
    DynamicFormEmailRegExp,
    DynamicFormTextFieldLabelPositions,
    DynamicFormTextFieldLabelPositionsNone,
    DynamicFormThirdPartyApiTypes,
    DynamicFormThirdPartyApiTypesCustom,
} from "@midland-industries/Types/DynamicFormModels";
import DynamicForm from "@midland-industries/Widgets/DynamicForms/DynamicForm";
import DynamicFormCheckbox from "@midland-industries/Widgets/DynamicForms/DynamicFormCheckbox";
import DynamicFormCheckboxGroup from "@midland-industries/Widgets/DynamicForms/DynamicFormCheckboxGroup";
import DynamicFormSelect from "@midland-industries/Widgets/DynamicForms/DynamicFormSelect";
import DynamicFormTextField from "@midland-industries/Widgets/DynamicForms/DynamicFormTextField";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    emailRecipients = "emailRecipients",
    successMessage = "successMessage",
    columns = "columns",
    enableRecaptcha = "enableRecaptcha",
    submitButtonText = "submitButtonText",
    buttonVariant = "buttonVariant",
    buttonShape = "buttonShape",
    buttonType = "buttonType",
    sendToThirdPartyApi = "sendToThirdPartyApi",
    sendToApiType = "sendToApiType",
    customApiEndpoint = "customApiEndpoint",
    endpointRequiresAuth = "endpointRequiresAuth",
    endpointUserId = "endpointUserId",
    endpointPassword = "endpointPassword",
    displayCustomApiResponse = "displayCustomApiResponse",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.emailRecipients]: string;
        [fields.successMessage]: string;
        [fields.columns]: number;
        [fields.enableRecaptcha]: boolean;
        [fields.submitButtonText]: string;
        [fields.buttonVariant]: DynamicFormButtonVariants;
        [fields.buttonShape]: DynamicFormButtonShapes;
        [fields.buttonType]: DynamicFormButtonTypes;
        [fields.sendToThirdPartyApi]: boolean;
        [fields.sendToApiType]: DynamicFormThirdPartyApiTypes;
        [fields.customApiEndpoint]: string;
        [fields.endpointRequiresAuth]: boolean;
        [fields.endpointUserId]: string;
        [fields.endpointPassword]: string;
        [fields.displayCustomApiResponse]: boolean;
    };
}

const mapStateToProps = (state: CustomDynamicFormState) => {
    const dynamicFormData = state.xcentiumDynamicForms;
    return {
        isCanada: getIsCanadaWebsite(state),
        dynamicFormData,
    };
};

const mapDispatchToProps = {
    setDynamicFormFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CustomerApplicationFormShortStyles {
    wrapper?: InjectableCss;
    formContainer?: GridContainerProps;
    labelGridItem?: GridItemProps;
    fieldGridItem?: GridItemProps;
    fullWidthGridItem?: GridItemProps;
    termsGridItem?: GridItemProps;
    termsLinkGridItem?: GridItemProps;
    termsLinkWrapper?: InjectableCss;
    termsAndConditionsLink?: LinkPresentationProps;
    labelWrapper?: InjectableCss;
    label?: TypographyPresentationProps;
    bodyTextWrapper?: InjectableCss;
    bodyText?: TypographyPresentationProps;
    bodyTextBold?: TypographyPresentationProps;
}

export const customerApplicationFormShortStyles: CustomerApplicationFormShortStyles = {
    wrapper: {
        css: css`
            width: 100%;
            margin-bottom: 30px;
        `,
    },
    formContainer: {
        gap: 0,
    },
    labelGridItem: {
        width: 4,
        css: css`
            align-items: flex-start;
            padding-bottom: 15px;
        `,
    },
    fieldGridItem: {
        width: 8,
        css: css`
            align-items: center;
            padding-bottom: 15px;
        `,
    },
    fullWidthGridItem: {
        width: 12,
        css: css`
            padding-bottom: 15px;
        `,
    },
    termsGridItem: {
        width: 4,
        css: css`
            align-items: center;
            padding-bottom: 15px;
        `,
    },
    termsLinkGridItem: {
        width: 12,
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            padding-bottom: 15px;
        `,
    },
    labelWrapper: {
        css: css`
            margin-top: 10px;
        `,
    },
    label: {
        css: css`
            font-weight: bold;
        `,
    },
    bodyTextWrapper: {},
    bodyText: {},
    bodyTextBold: {
        css: css`
            font-weight: bold;
        `,
    },
};

const styles = customerApplicationFormShortStyles;

const CustomerApplicationFormShort: FC<Props> = props => {
    const { id, fields, dynamicFormData, isCanada } = props;
    const { setDynamicFormFieldValue } = props;
    const {
        emailRecipients,
        successMessage,
        columns,
        enableRecaptcha,
        submitButtonText,
        buttonVariant,
        buttonShape,
        buttonType,
        sendToThirdPartyApi,
        sendToApiType,
        customApiEndpoint,
        endpointRequiresAuth,
        endpointUserId,
        endpointPassword,
        displayCustomApiResponse,
    } = fields;

    const formName = "Customer Application Form Short";
    
    return (
        <StyledWrapper {...styles.wrapper}>
            <DynamicForm
                id={id}
                fields={{
                    formName,
                    emailRecipients,
                    submitButtonText,
                    successMessage,
                    buttonVariant,
                    buttonShape,
                    buttonType,
                    showResetButton: false,
                    enableRecaptcha,
                    sendToThirdPartyApi,
                    sendToApiType,
                    customApiEndpoint,
                    endpointRequiresAuth,
                    endpointUserId,
                    endpointPassword,
                    displayCustomApiResponse,
                }}
            >
                <GridContainer {...styles.formContainer}>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Company Name *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "company_name",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Company Name",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 200,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Doing Business As (DBA)")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "dba",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Doing Business As (DBA)",
                                hintText: "",
                                isRequired: false,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 200,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Full Name *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "full_name",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Full Name",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: false,
                                maxLength: 0,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Email *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "email",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Email",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 254,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: true,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Address *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "address",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Address",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 300,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("City *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "city",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "City",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 200,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("State *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "state",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "State",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 200,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Country *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormSelect
                            fields={{
                                key: "country",
                                hasLabel: false,
                                label: "Country",
                                hintText: "",
                                isRequired: true,
                                options: [
                                    {
                                        fields: {
                                            label: "United States of America",
                                            value: "US",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Canada",
                                            value: "CA",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Mexico",
                                            value: "MX",
                                        },
                                    },
                                ],
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Postal Code *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "postal_code",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Postal Code",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 30,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Purchasing Name *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "purchasing_name",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Purchasing Name",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 200,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Purchasing Email *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "purchasing_email",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Purchasing Email",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 254,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: true,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>
                                {translate("What Buying Groups do you belong to? *")}
                            </Typography>
                            <br />
                            <Typography>{translate("Please select all that apply.")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormCheckboxGroup
                            fields={{
                                key: "buying_groups[]",
                                hasLabel: false,
                                label: "What Buying Groups do you belong to?",
                                columns: 2,
                                requireOneSelection: true,
                                sendOnlyIfChecked: true,
                                topics: [
                                    {
                                        fields: {
                                            key: "1",
                                            isCheckedByDefault: false,
                                            labelPosition: DynamicFormCheckboxGroupLabelPositionsRight,
                                            label: "AD",
                                            changeTrueFalseToYesNo: true,
                                        },
                                    },
                                    {
                                        fields: {
                                            key: "2",
                                            isCheckedByDefault: false,
                                            labelPosition: DynamicFormCheckboxGroupLabelPositionsRight,
                                            label: "IDCO",
                                            changeTrueFalseToYesNo: true,
                                        },
                                    },
                                    {
                                        fields: {
                                            key: "3",
                                            isCheckedByDefault: false,
                                            labelPosition: DynamicFormCheckboxGroupLabelPositionsRight,
                                            label: "IDC",
                                            changeTrueFalseToYesNo: true,
                                        },
                                    },
                                    {
                                        fields: {
                                            key: "4",
                                            isCheckedByDefault: false,
                                            labelPosition: DynamicFormCheckboxGroupLabelPositionsRight,
                                            label: "Net Plus Alliance",
                                            changeTrueFalseToYesNo: true,
                                        },
                                    },
                                    {
                                        fields: {
                                            key: "5",
                                            isCheckedByDefault: false,
                                            labelPosition: DynamicFormCheckboxGroupLabelPositionsRight,
                                            label: "DPA",
                                            changeTrueFalseToYesNo: true,
                                        },
                                    },
                                    {
                                        fields: {
                                            key: "6",
                                            isCheckedByDefault: false,
                                            labelPosition: DynamicFormCheckboxGroupLabelPositionsRight,
                                            label: "IBC",
                                            changeTrueFalseToYesNo: true,
                                        },
                                    },
                                    {
                                        fields: {
                                            key: "10",
                                            isCheckedByDefault: false,
                                            labelPosition: DynamicFormCheckboxGroupLabelPositionsRight,
                                            label: "HDA",
                                            changeTrueFalseToYesNo: true,
                                        },
                                    },
                                    {
                                        fields: {
                                            key: "11",
                                            isCheckedByDefault: false,
                                            labelPosition: DynamicFormCheckboxGroupLabelPositionsRight,
                                            label: "VIPAR",
                                            changeTrueFalseToYesNo: true,
                                        },
                                    },
                                    {
                                        fields: {
                                            key: "13",
                                            isCheckedByDefault: false,
                                            labelPosition: DynamicFormCheckboxGroupLabelPositionsRight,
                                            label: "IMARK",
                                            changeTrueFalseToYesNo: true,
                                        },
                                    },
                                    {
                                        fields: {
                                            key: "14",
                                            isCheckedByDefault: false,
                                            labelPosition: DynamicFormCheckboxGroupLabelPositionsRight,
                                            label: "TDG",
                                            changeTrueFalseToYesNo: true,
                                        },
                                    },
                                    {
                                        fields: {
                                            key: "15",
                                            isCheckedByDefault: false,
                                            labelPosition: DynamicFormCheckboxGroupLabelPositionsRight,
                                            label: "HARDI",
                                            changeTrueFalseToYesNo: true,
                                        },
                                    },
                                    {
                                        fields: {
                                            key: "16",
                                            isCheckedByDefault: false,
                                            labelPosition: DynamicFormCheckboxGroupLabelPositionsRight,
                                            label: "None",
                                            changeTrueFalseToYesNo: true,
                                        },
                                    },
                                ],
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Electronic Signature *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "applicant_name",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Electronic Signature",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 300,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Accept Terms *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.termsGridItem}>
                        <DynamicFormCheckbox
                            fields={{
                                key: "accept_terms",
                                group: "",
                                labelPosition: DynamicFormCheckboxLabelPositionsNone,
                                label: "Accept Terms",
                                isRequired: true,
                                isCheckedByDefault: false,
                                changeTrueFalseToYesNo: true,
                                hintText: "",
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.termsLinkGridItem}>
                        <StyledWrapper {...styles.termsLinkWrapper}>
                            <Link {...styles.termsAndConditionsLink} href="/terms-and-conditions" target="_blank">
                                Terms and Conditions
                            </Link>
                        </StyledWrapper>
                    </GridItem>
                </GridContainer>
                <StyledWrapper {...styles.bodyTextWrapper}>
                    <Zone contentId={id} zoneName="customerApplicationFormShortZone2" />
                </StyledWrapper>
            </DynamicForm>
        </StyledWrapper>
    );
};

const formTab = {
    displayName: "Form",
    sortOrder: 0,
};

const buttonTab = {
    displayName: "Button",
    sortOrder: 1,
};

const actionTab = {
    displayName: "Actions",
    sortOrder: 2,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CustomerApplicationFormShort),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.emailRecipients,
                displayName: "Email recipients",
                editorTemplate: "MultilineTextField",
                fieldType: "General",
                defaultValue: "testaccount@insitesoft.com",
                isRequired: true,
                regularExpression: DynamicFormEmailRegExp,
                tab: formTab,
                sortOrder: 1,
            },
            {
                name: fields.successMessage,
                displayName: "Submit Success Message",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                defaultValue: "Thank you for your inquiry. A customer service representative will contact you shortly.",
                tab: formTab,
                sortOrder: 2,
            },
            {
                name: fields.columns,
                displayName: "Columns",
                editorTemplate: "IntegerField",
                defaultValue: 1,
                fieldType: "General",
                isRequired: true,
                min: 1,
                max: 3,
                tooltip: "How many columns to use to display the checkbox group. Min 1. Max 3.",
                sortOrder: 3,
                tab: formTab,
            },
            {
                name: fields.enableRecaptcha,
                displayName: "Include ReCaptcha",
                tooltip: "Enabling this setting adds a ReCaptcha element to the form which must be clicked to submit.",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
                sortOrder: 4,
                tab: formTab,
            },
            {
                name: fields.submitButtonText,
                displayName: "Submit Button Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Submit",
                tab: buttonTab,
                sortOrder: 100,
            },
            {
                name: fields.buttonVariant,
                displayName: "Select Button Variant",
                editorTemplate: "DropDownField",
                defaultValue: "primary",
                fieldType: "General",
                options: [
                    {
                        displayName: "Primary",
                        value: "primary",
                    },
                    {
                        displayName: "Secondary",
                        value: "secondary",
                    },
                    {
                        displayName: "Tertiary",
                        value: "tertiary",
                    },
                ],
                hideEmptyOption: true,
                isRequired: true,
                tab: buttonTab,
                sortOrder: 105,
            },
            {
                name: fields.buttonShape,
                displayName: "Select Button Shape",
                editorTemplate: "DropDownField",
                defaultValue: "rectangle",
                fieldType: "General",
                options: [
                    {
                        displayName: "Rectangle",
                        value: "rectangle",
                    },
                    {
                        displayName: "Pill",
                        value: "pill",
                    },
                    {
                        displayName: "Rounded",
                        value: "rounded",
                    },
                ],
                hideEmptyOption: true,
                isRequired: true,
                tab: buttonTab,
                sortOrder: 110,
            },
            {
                name: fields.buttonType,
                displayName: "Select Button Type",
                editorTemplate: "DropDownField",
                defaultValue: "solid",
                fieldType: "General",
                options: [
                    {
                        displayName: "Solid",
                        value: "solid",
                    },
                    {
                        displayName: "Outline",
                        value: "outline",
                    },
                ],
                hideEmptyOption: true,
                isRequired: true,
                tab: buttonTab,
                sortOrder: 115,
            },
            {
                name: fields.sendToThirdPartyApi,
                displayName: "Upload FormData to 3rd Party System",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
                sortOrder: 200,
                tab: actionTab,
            },
            {
                name: fields.sendToApiType,
                displayName: "Select 3rd Party System Type",
                editorTemplate: "DropDownField",
                defaultValue: DynamicFormThirdPartyApiTypesCustom,
                fieldType: "General",
                options: [
                    {
                        displayName: "Custom API Endpoint",
                        value: DynamicFormThirdPartyApiTypesCustom,
                    },
                ],
                hideEmptyOption: true,
                isRequired: true,
                isVisible: widget => widget.fields.sendToThirdPartyApi,
                tab: actionTab,
                sortOrder: 210,
            },
            {
                name: fields.customApiEndpoint,
                displayName: "Custom API Endpoint URL",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
                isVisible: widget => widget.fields.sendToThirdPartyApi && widget.fields.sendToApiType === "custom-api",
                tab: actionTab,
                sortOrder: 220,
            },
            {
                name: fields.endpointRequiresAuth,
                displayName: "Upload Requires Authentication",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
                isVisible: widget => widget.fields.sendToThirdPartyApi,
                tab: actionTab,
                sortOrder: 221,
            },
            {
                name: fields.endpointUserId,
                displayName: "User Id",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
                isVisible: widget => widget.fields.endpointRequiresAuth,
                tab: actionTab,
                sortOrder: 222,
            },
            {
                name: fields.endpointPassword,
                displayName: "Password",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
                isVisible: widget => widget.fields.endpointRequiresAuth,
                tab: actionTab,
                sortOrder: 223,
            },
            {
                name: fields.displayCustomApiResponse,
                displayName: "Display results from API below Form for testing",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
                sortOrder: 230,
                tab: actionTab,
            },
        ],
    },
};

export default widgetModule;
